import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { openTopLevel } from "@/lib/topNavigation";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { translateError } from "@/i18n/errors";

const DriveContext = createContext(null);
const RETURN = { connected: "drive.connected", refused: "drive.refused", error: "drive.oauthError", scope: "drive.scopeMissing", expired: "drive.oauthError" };

// Per-space Google Drive connection state (admin or the logged-in developer).
export const DriveProvider = ({ children }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);

  const refresh = useCallback(() => api.get(`${base}/drive/config`).then(({ data }) => setCfg(data)).catch(() => setCfg({ enabled: false })), [base]);
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const ret = params.get("drive");
    if (!ret) return;
    (ret === "connected" ? toast.success : toast.error)(t(RETURN[ret] || "drive.oauthError"));
    params.delete("drive");
    setParams(params, { replace: true });
  }, [params, setParams, t]);

  const connect = async () => {
    try {
      const { data } = await api.post(`${base}/drive/connect`);
      openTopLevel(data.url);
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  const disconnect = async () => {
    await api.delete(`${base}/drive/connect`);
    toast.success(t("drive.disconnected"));
    refresh();
  };
  return <DriveContext.Provider value={{ cfg, refresh, connect, disconnect }}>{children}</DriveContext.Provider>;
};

export const useDrive = () => useContext(DriveContext);
